import axios from "axios";
import { DataGrid } from "devextreme-react";
import {
  Column,
  Editing,
  Form,
  LoadPanel,
  Popup,
  Scrolling,
  SearchPanel,
  Selection,
  Texts,
} from "devextreme-react/data-grid";
import { Label, SimpleItem } from "devextreme-react/form";
import CustomStore from "devextreme/data/custom_store";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import TwoDateBoxComponent2 from "./TwoDateBoxComponent2";

export default function LeaveOfAbsenceInformation() {
  const [dataSource, setDataSource] = useState(null);
  const [formDataSource, setFormDataSource] = useState(null);

  const userInfo = useSelector((state) => state.userInfo);

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const leaveOfAbsenceInfoGridRef = useRef(null);

  useEffect(() => {
    setDataSource(
      new CustomStore({
        key: "SEQ",
        load: async () => {
          const result = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/lvgAbsInfo/${userInfo.employeeNo}`
          );

          return result.data;
        },
        insert: async (values) => {
          values = {
            ...values,
            startDate: startDateRef.current.instance.option("value"),
            endDate: endDateRef.current.instance.option("value"),
          };

          await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/lvgAbsInfo/${userInfo.employeeNo}`,
            values
          );
        },
        update: async (key, values) => {
          values = {
            ...values,
            startDate: startDateRef.current.instance.option("value"),
            endDate: endDateRef.current.instance.option("value"),
          };

          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/lvgAbsInfo/${userInfo.employeeNo}/${key}`,
            values
          );
        },
        remove: async (key) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/lvgAbsInfo/${userInfo.employeeNo}/${key}`
          );
        },
        errorHandler: (error) => {
          if (error.message == "Request failed with status code 409") {
            error.message = `The leave of absence information is duplicated.`;
          }
        },
      })
    );
  }, []);

  const onEditingStart = (e) => {
    if (e.data.startDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        startDate: e.data.startDate,
      }));
    }

    if (e.data.endDate) {
      setFormDataSource((prevState) => ({
        ...prevState,
        endDate: e.data.endDate,
      }));
    }
  };

  const onSaving = React.useCallback(async (e) => {
    if (e.changes.length == 0) {
      const SEQ =
        leaveOfAbsenceInfoGridRef.current.instance.option("editing.editRowKey");

      const values = {
        startDate: startDateRef.current.instance.option("value"),
        endDate: endDateRef.current.instance.option("value"),
      };

      await axios.put(
        `${process.env.REACT_APP_BECCO_API_URL}/api/persRcrd/personnelInfo/lvgAbsInfo/${userInfo.employeeNo}/${SEQ}`,
        values
      );

      leaveOfAbsenceInfoGridRef.current.instance.refresh();
    } else {
      if (!startDateRef.current.instance.option("value")) {
        startDateRef.current.instance.option("validationStatus", "invalid");

        e.cancel = true;
      }
    }
  }, []);

  const onInitNewRow = (e) => {
    const key = leaveOfAbsenceInfoGridRef.current.instance.getKeyByRowIndex(0);

    if (key) {
      e.component.selectRows(key, true);
      e.data =
        leaveOfAbsenceInfoGridRef.current.instance.getSelectedRowsData()[0];

      if (e.data.startDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          startDate: e.data.startDate,
        }));
      }

      if (e.data.endDate) {
        setFormDataSource((prevState) => ({
          ...prevState,
          endDate: e.data.endDate,
        }));
      }
    } else {
      setFormDataSource(null);
    }
  };

  return (
    <DataGrid
      id="leaveOfAbsenceInfoGrid"
      ref={leaveOfAbsenceInfoGridRef}
      dataSource={dataSource}
      keyExpr="SEQ"
      rowAlternationEnabled={true}
      showBorders={true}
      showColumnLines={true}
      allowColumnResizing={true}
      columnAutoWidth={true}
      wordWrapEnabled={true}
      allowColumnReordering={true}
      style={{
        maxHeight: "calc(100vh - 180px)",
        marginLeft: "10px",
        marginRight: "10px",
      }}
      hoverStateEnabled={true}
      showColumnHeaders={true}
      onEditingStart={onEditingStart}
      onSaving={onSaving}
      onInitNewRow={onInitNewRow}
    >
      <LoadPanel enabled={true} />

      <SearchPanel visible={true} />

      <Scrolling mode={"virtual"} showScrollbar={"always"} />

      <Selection mode={"single"} />

      <Editing
        mode={"popup"}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
      >
        <Texts
          confirmDeleteTitle={"LEAVE OF ABSENCE INFO."}
          confirmDeleteMessage="Are you sure want to delete this leave of absence info?"
        />
        <Popup
          title={"LEAVE OF ABSENCE INFO."}
          showTitle={true}
          height={"auto"}
          width={1200}
        />
        <Form
          labelMode={"outside"}
          showColonAfterLabel={false}
          labelLocation="top"
          minColWidth={300}
          colCount={2}
          colCountByScreen={{ sm: 2, md: 4 }}
          screenByWidth={(width) => {
            return width < 720 ? "sm" : "md";
          }}
          validationGroup="leaveOfAbsenceInfoGroup"
          height={"100%"}
        >
          <SimpleItem>
            <Label text={"Leave of Absence Period"} />
            <TwoDateBoxComponent2
              firstDateBox={{
                placeholder: "Leave of Absence Start Date",
                field: "startDate",
                ref: startDateRef,
                requiredRuleMessage:
                  "* The leave of absence start date is required.",
              }}
              secondDateBox={{
                placeholder: "Leave of Absence End Date",
                field: "endDate",
                ref: endDateRef,
              }}
              formDataSource={formDataSource}
            />
          </SimpleItem>
          <SimpleItem
            dataField={"reasonLvgAbs"}
            editorType="dxTextBox"
            editorOptions={{
              showClearButton: true,
            }}
            colSpan={3}
          >
            <Label text={"Reason for Leave of Absence"} />
          </SimpleItem>
        </Form>
      </Editing>

      <Column
        dataField={"startDate"}
        caption="Leave of Absence Start Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={200}
        alignment={"center"}
      />
      <Column
        dataField={"endDate"}
        caption="Leave of Absence End Date"
        dataType="date"
        format="yyyy-MM-dd"
        editorOptions={{
          dateSerializationFormat: "yyyy-MM-dd",
        }}
        width={200}
        alignment={"center"}
      />
      <Column
        dataField={"reasonLvgAbs"}
        caption={"Reason for Leave of Absence"}
      />
    </DataGrid>
  );
}
